// ---------------------------------------------------------------------------
// Section catalog — the curated set of section types HireIQ supports.
// Each entry maps a stable `key` (matches the resume AI JSON schema field
// in app/services/ai_resume.py and the renderer in CompanyResumeView) to
// a human label, structural type, AI extraction fields, and helper copy.
// Admins can add/remove sections from this catalog; custom keys are not
// supported because the AI parser and renderer are both schema-bound.
// ---------------------------------------------------------------------------
export const SECTION_CATALOG = [
  {
    key: 'summary',
    label: 'Professional Summary',
    type: 'paragraph',
    description: '2–4 sentence positioning paragraph.',
    fields: [],
  },
  {
    key: 'key_highlights',
    label: 'Key Highlights',
    type: 'list',
    description: 'Up to 3 short bullets on the client-report Profile Summary card.',
    fields: [],
  },
  {
    key: 'skills',
    label: 'Core Skills',
    type: 'pairs',
    description: 'Grouped chips: Languages · Platforms · Practices.',
    fields: ['category', 'items'],
  },
  {
    key: 'tools_technologies',
    label: 'Key Tools & Technologies',
    type: 'pairs',
    description: 'Hands-on tools by category (Backend, Cloud & DevOps, Data, Frontend…).',
    fields: ['category', 'items'],
  },
  {
    key: 'experience',
    label: 'Past Companies & Experience',
    type: 'pairs',
    description: 'Companies, roles, dates, and impact bullets.',
    fields: ['company', 'role', 'start', 'end', 'location', 'bullets'],
  },
  {
    key: 'projects',
    label: 'Key Projects',
    type: 'pairs',
    description: 'Notable projects with role, year, and summary.',
    fields: ['name', 'role', 'year', 'summary'],
  },
  {
    key: 'awards',
    label: 'Awards & Recognitions',
    type: 'pairs',
    description: 'Honours, hackathon wins, employee awards, scholarships.',
    fields: ['title', 'issued_by', 'year', 'summary'],
  },
  {
    key: 'certifications',
    label: 'Certifications',
    type: 'pairs',
    description: 'Cloud, framework, or vendor certifications.',
    fields: ['name', 'issued_by', 'year'],
  },
  {
    key: 'education',
    label: 'Academics & Education',
    type: 'pairs',
    description: 'Degrees, institutions, years, highlights.',
    fields: ['degree', 'institution', 'year', 'highlights'],
  },
  {
    key: 'publications',
    label: 'Publications & Talks',
    type: 'pairs',
    description: 'Conference talks, papers, blog posts.',
    fields: ['title', 'venue', 'year', 'url'],
  },
  {
    key: 'languages',
    label: 'Spoken Languages',
    type: 'list',
    description: 'Human languages (e.g. English, Hindi).',
    fields: [],
  },
];

export const SECTION_BY_KEY = Object.fromEntries(SECTION_CATALOG.map((s) => [s.key, s]));

// ---------------------------------------------------------------------------
// Sprint 11.3d — Default AI parser prompt + per-section AI hints.
// These mirror /app/backend/app/services/ai_resume.py and are used as the
// "Reset to default" / "Use default" seeds in the editor. The backend uses
// the same defaults when the admin has not overridden them.
// ---------------------------------------------------------------------------
export const DEFAULT_PARSER_INSTRUCTIONS = `You are an HR resume parser. Read the raw resume text and emit the candidate's profile as a single JSON object matching the provided schema. Be faithful to the source: never invent dates, employers, or skills. If a section is not present in the source, omit it or return an empty list. Keep wording concise and professional.

EXTRACTION RULES (follow ALL of these — they keep the output complete):
1. Always populate \`tools_technologies\` with 3–6 categorised groups whenever the resume mentions technical tools, frameworks, languages, or platforms — even if the resume itself does not group them. Use sensible categories (e.g. 'Languages', 'Backend & Frameworks', 'Cloud & DevOps', 'Data & Storage', 'Frontend', 'Tools & Monitoring'). This catalog is required for IT resumes.
2. Also fill \`skills.languages\`, \`skills.platforms\`, \`skills.practices\` as a compact summary alongside \`tools_technologies\`. Both are required when technical content exists.
3. Cap bullets per experience role at 3 short bullets (max 25 words each). Lead each bullet with a strong verb and include a quantified outcome where the source supports it (faithfully — never invent metrics).
4. Projects vs experience: if a section is titled 'Projects' or lists named projects with summary text, put them under \`projects\`. Production roles at companies belong in \`experience\`.
5. Awards include hackathon wins, ranks (e.g. 'JEE Rank 13,341'), coding milestones (e.g. '250+ LeetCode solved'), employee recognitions, scholarships, academic honours.
6. \`languages\` is for spoken human languages only (English, Hindi, Spanish). Programming languages go in \`tools_technologies\` / \`skills.languages\`.
7. Normalise date formats to 'MMM YYYY' (e.g. 'Mar 2023'). Standardise role titles to industry-standard casing.
8. If the resume omits a Professional Summary, synthesise a 2–3 sentence summary from the most recent role + headline + dominant tech stack — labelled as inference from the source, never invented facts.`;

export const DEFAULT_AI_HINTS = {
  summary: "If a Summary/Profile/Objective section exists, paraphrase it faithfully in 2–4 sentences. If missing, synthesise from the most recent role + headline + dominant tech. Lead with seniority and primary domain.",
  key_highlights: "Emit exactly 3 short bullets (≤25 words each) that a recruiter can scan in seconds. Prefer concrete stack depth, shipped products, and quantified outcomes grounded in the source resume. Do not invent facts. Distinct from the prose summary — these are discrete checklist lines for the Profile Summary card.",
  skills: "Compact legacy summary of skills. Populate languages / platforms / practices with the 3-10 most prominent items each. Companion to tools_technologies.",
  tools_technologies: "ALWAYS emit 3–6 categorised groups when any technical content is mentioned. Prefer these categories where applicable: Languages, Backend & Frameworks, Cloud & DevOps, Data & Storage, Frontend, Testing & Monitoring, ML/AI, Domain Platforms. Each item should be a real tool/framework name (e.g. 'FastAPI', 'PostgreSQL'), not a vague concept.",
  experience: "List roles newest-first. For each: role, company, start, end (or 'Present'), location, and 3 short impact-driven bullets (verb + outcome, ≤25 words). Standardise dates to 'MMM YYYY'.",
  projects: "Include named projects from a Projects/Portfolio/Side-projects section. Each item: name, role, year, 1-2 sentence summary mentioning the tech stack.",
  awards: "Faithfully extract hackathon wins, competitive-exam ranks (JEE, GATE), coding-platform milestones (LeetCode/Codeforces ranks), employee recognitions, scholarships, academic honours (Dean's List). Include issued_by + year.",
  certifications: "Cloud, framework, vendor, or domain certifications. Include issuer + year. Do not duplicate items here that belong under tools_technologies.",
  education: "Degrees, diplomas, schools. Include degree, institution, year(s), and any highlights (GPA, honours, relevant coursework).",
  publications: "Conference talks, journal/blog publications, papers, patents. Include title, venue, year, and URL when present.",
  languages: "Spoken human languages ONLY (English, Hindi, Spanish, Marathi, etc.). Never list programming languages here — those belong in tools_technologies.",
};

// ---------------------------------------------------------------------------
// Static sample used to render the right-side live preview.
// Includes data for every catalog section so admins can preview enabling them.
// ---------------------------------------------------------------------------
export const SAMPLE_PREVIEW = {
  generated_at: 'just now',
  header: {
    full_name: 'Aanya Kapoor',
    headline: 'Senior Backend Engineer · Distributed Systems',
    location: 'Bengaluru, India',
    email: 'aanya.kapoor@example.com',
    phone: '+91 98010 12341',
    linkedin: 'linkedin.com/in/aanya-kapoor',
  },
  summary: 'Senior backend engineer with 5+ years building distributed systems in Python and Go. Track record of scaling event pipelines from thousands to millions of events/day.',
  key_highlights: [
    'Senior backend engineer with hands-on depth in Python, Go, Kafka, and PostgreSQL.',
    'Re-architected notifications platform to deliver 2M events/day with p99 under 250ms.',
    'Built HIPAA-compliant patient-records service in Go with 99.95% uptime SLO.',
  ],
  skills: {
    languages: ['Python', 'Go', 'SQL', 'TypeScript'],
    platforms: ['AWS', 'Kafka', 'PostgreSQL'],
    practices: ['System Design', 'TDD', 'On-call leadership'],
  },
  tools_technologies: [
    { category: 'Backend', items: ['FastAPI', 'Django', 'gRPC', 'Celery'] },
    { category: 'Cloud & DevOps', items: ['AWS (ECS, Lambda, SQS)', 'Terraform', 'GitHub Actions', 'Datadog'] },
    { category: 'Data', items: ['Kafka', 'PostgreSQL', 'Redis', 'ClickHouse'] },
    { category: 'Frontend', items: ['React', 'Tailwind', 'Vite'] },
  ],
  experience: [
    {
      company: 'Northwind Logistics',
      role: 'Senior Backend Engineer',
      start: 'Mar 2023', end: 'Present', location: 'Bengaluru',
      bullets: [
        'Re-architected notifications platform to deliver 2M events/day with p99 < 250ms.',
        'Led on-call rotation for the payments domain; cut paging volume by 38%.',
      ],
    },
    {
      company: 'Vantage Health',
      role: 'Backend Engineer',
      start: 'Jul 2020', end: 'Feb 2023', location: 'Pune',
      bullets: [
        'Built HIPAA-compliant patient-records service in Go with 99.95% uptime SLO.',
      ],
    },
  ],
  projects: [
    { name: 'Notifications v2', role: 'Tech lead', year: '2024', summary: 'Kafka-backed fan-out service with Postgres outbox + Go consumers.' },
    { name: 'OpenAPI Gateway', role: 'Maintainer', year: '2023', summary: 'OSS reverse-proxy templated for FastAPI services; 1.2k GitHub stars.' },
  ],
  awards: [
    { title: 'Engineering Excellence Award', issued_by: 'Northwind Logistics', year: '2024', summary: 'For the notifications re-architecture.' },
    { title: 'HackBangalore — 1st place', issued_by: 'HackBangalore', year: '2022', summary: 'Real-time public-transit ETA prediction.' },
  ],
  certifications: [
    { name: 'AWS Solutions Architect — Associate', issued_by: 'AWS', year: '2024' },
    { name: 'Certified Kubernetes Administrator', issued_by: 'CNCF', year: '2023' },
  ],
  education: [
    { degree: 'B.Tech, Computer Science', institution: 'IIT Bombay', year: '2017–2021', highlights: "Dean's List · GPA 9.1/10" },
  ],
  publications: [
    { title: 'Pragmatic outbox patterns in Postgres', venue: 'Northwind Engineering Blog', year: '2024', url: 'https://example.com/outbox' },
    { title: 'Scaling Kafka consumers in Go', venue: 'GopherCon India', year: '2023' },
  ],
  languages: ['English', 'Hindi', 'Marathi'],
};
